import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur, deDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Plus, Printer, Share2, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SignaturePad } from "../../components/SignaturePad";
import { VerkaufAusAnkauf } from "./VerkaufAusAnkauf";
import { toast } from "sonner";

export default function Ankauf() {
  const [purchases, setPurchases] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sellOpen, setSellOpen] = useState(false);

  const load = () => {
    api.get("/purchases").then((r) => setPurchases(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/purchases/${id}`);
    setDetail(data);
  };

  const sign = async (sig) => {
    await api.post(`/purchases/${detail.id}/sign`, { signature: sig });
    toast.success("Vertrag wurde digital unterschrieben.");
    openDetail(detail.id);
    load();
  };

  const printPdf = async (contractId) => {
    const { data } = await api.get(`/contracts/${contractId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    window.open(url, "_blank");
  };

  const openPdf = async (path) => {
    const { data } = await api.get(path, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
  };

  const sharePdf = async (contractId, number) => {
    try {
      const { data } = await api.get(`/contracts/${contractId}/pdf`, { responseType: "blob" });
      const file = new File([data], `${number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Kaufvertrag ${number}` });
      } else {
        window.open(URL.createObjectURL(file), "_blank");
        toast.info("Teilen nicht verfügbar – PDF wurde geöffnet.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Teilen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="ankauf-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Ankauf</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Ankaufshistorie</h1>
        </div>
        <Link to="/app/ankauf/neu">
          <Button className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-purchase-button"><Plus className="w-4 h-4 mr-2" />Neuer Ankauf</Button>
        </Link>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-zinc-100 text-left">
              <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Nummer</th>
              <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Datum</th>
              <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Verkäufer</th>
              <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Positionen</th>
              <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Gesamt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {loading && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400 font-mono text-xs">Laden…</td></tr>}
            {!loading && purchases.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">Noch keine Ankäufe. Starten Sie mit „Neuer Ankauf“.</td></tr>}
            {purchases.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p.id)} data-testid={`purchase-row-${p.number}`} className="hover:bg-zinc-50 cursor-pointer transition-colors">
                <td className="px-5 py-3 font-mono text-xs">{p.number}</td>
                <td className="px-5 py-3 text-zinc-600">{deDate(p.date)}</td>
                <td className="px-5 py-3">{p.seller_name}</td>
                <td className="px-5 py-3 text-zinc-500">{p.positions?.map((x) => `${x.qty}× ${x.name}`).join(", ")}</td>
                <td className="px-5 py-3 text-right font-medium">{eur(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-base flex items-center justify-between gap-3 flex-wrap">
                  <span>Ankauf {detail.number}</span>
                  <Button size="sm" onClick={() => setSellOpen(true)} data-testid="purchase-sell-button" className="bg-zinc-900 hover:bg-zinc-800 text-white">
                    Als Verkauf erfassen
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Verkäufer</div>
                    <div className="mt-1">{detail.seller?.first_name} {detail.seller?.last_name} <span className="font-mono text-xs text-zinc-400">{detail.seller?.number}</span></div></div>
                  <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Zahlungsart</div><div className="mt-1 capitalize">{detail.payment_method}</div></div>
                </div>
                <div className="border border-zinc-100 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 text-left"><th className="px-3 py-2 text-xs font-mono text-zinc-400">Artikel</th><th className="px-3 py-2 text-xs font-mono text-zinc-400">Menge</th><th className="px-3 py-2 text-xs font-mono text-zinc-400 text-right">Preis</th></tr></thead>
                    <tbody>
                      {detail.positions?.map((p, i) => (
                        <tr key={i} className="border-t border-zinc-50"><td className="px-3 py-2">{p.name}</td><td className="px-3 py-2">{p.qty}</td><td className="px-3 py-2 text-right">{eur(p.price)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between items-center border-t border-zinc-100 pt-4">
                  <span className="font-medium">Gesamt</span>
                  <span className="font-display text-xl font-semibold">{eur(detail.total)}</span>
                </div>
                {detail.contract && (
                  <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/50 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono text-xs text-zinc-500">Vertrag {detail.contract.number}</div>
                        <span className={`inline-block mt-1 text-[10px] font-mono px-2 py-0.5 rounded-full border ${detail.contract.status === "unterschrieben" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-700 border-zinc-300"}`} data-testid="contract-status-badge">
                          {detail.contract.status === "unterschrieben" ? "Unterschrieben" : "Entwurf"}
                        </span>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => printPdf(detail.contract.id)} data-testid="contract-pdf-button" className="min-h-[44px]">
                        <Printer className="w-4 h-4 mr-1" /> PDF / Drucken
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => sharePdf(detail.contract.id, detail.contract.number)} data-testid="contract-share-button" className="min-h-[44px]">
                        <Share2 className="w-4 h-4 mr-1" /> Teilen
                      </Button>
                    </div>
                    {detail.contract.status === "entwurf" ? (
                      <SignaturePad onConfirm={sign} confirmLabel="Vertrag unterschreiben" />
                    ) : detail.contract.signature ? (
                      <img src={detail.contract.signature} alt="Unterschrift" className="h-20 border border-zinc-200 rounded-lg bg-white" data-testid="contract-signature-image" />
                    ) : null}
                  </div>
                )}
                <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/50">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Interne Dokumente (nicht für Kunden)</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => openPdf(`/purchases/${detail.id}/beleg-pdf`)} data-testid="purchase-beleg-button" className="min-h-[44px]"><Printer className="w-4 h-4 mr-1" /> Ankaufbeleg (INTERN)</Button>
                    <Button variant="outline" size="sm" onClick={() => openPdf(`/purchases/${detail.id}/auszahlung-pdf`)} data-testid="purchase-auszahlung-button" className="min-h-[44px]"><Printer className="w-4 h-4 mr-1" /> Auszahlungsbon (INTERN)</Button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Artikel ({detail.articles?.length})</div>
                  <div className="space-y-1">
                      {detail.articles?.map((a) => (
                      <div key={a.id} className="text-xs border border-zinc-100 rounded-lg px-3 py-2">
                        <div className="flex justify-between">
                          <span className="font-mono">{a.number}</span>
                          <span>{a.name}</span>
                          <span className="font-mono text-zinc-400">{a.serial || a.imei || "–"}</span>
                          <span className="font-medium">{eur(a.purchase_price)}</span>
                        </div>
                        {a.photos?.length > 0 && (
                          <div className="flex gap-1.5 mt-2 items-center">
                            <ImageIcon className="w-3.5 h-3.5 text-zinc-400" />
                            {a.photos.map((ph, pi) => (
                              <img key={pi} src={ph.data} alt={ph.label} className="w-10 h-10 object-cover rounded border border-zinc-200" />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {detail && (
        <VerkaufAusAnkauf purchase={detail} open={sellOpen} onClose={() => { setSellOpen(false); openDetail(detail.id); }}
          onDone={() => load()} />
      )}
    </div>
  );
}
